/**
 * @file Deplacement du chat chef : direction manuelle (doigt/joystick, prioritaire) ou AutoMove vers la cible ; bornes d'arene et decors solides.
 */
import type { SimContext } from '../Context';
import { isShootable } from '../combat/damage';
import type { With } from '../ecs/World';
import { dist, rotateToward } from '../math/geometry';
import { clampToBounds, resolveSolids, separateCats } from './movement';
import type { System } from './System';

const MANUAL_DEADZONE = 0.15;
const MOVING_SPEED = 0.2;
const UNARMED_RANGE = 8;
const HOME_SPEED_RATIO = 0.5;
const HOME_ARRIVE = 0.5;
/** Vitesse de rotation max du cap du chef (rad/s) : plus de demi-tour instantane. */
const HEADING_TURN_SPEED = 3 * Math.PI;
const HEADING_MOVE_EPS = 0.03;
/** Distance d'arret minimale devant une cible. */
const MIN_STOP = 1.5;

/** Point de retour de l'AutoMove sans cible : bas de l'arene en Main, centre ailleurs. */
/** Point de retour par defaut (le mode peut fournir le sien : Main = debut du troncon de chemin courant). */
const defaultHome = (ctx: SimContext): { x: number; y: number } =>
  ctx.modes.currentId === 'main' ? { x: 0, y: -ctx.modes.rules.arena.halfH * 0.5 } : { x: 0, y: 0 };

/**
 * Distance d'arret devant la cible : approachRatio x portee de l'arme, mais pour que TOUS les chats soient a portee sans quitter
 * leur place de formation (les suiveurs sont en retrait derriere le chef) : min sur l'equipe de (portee x ratio + retrait).
 */
const teamStopDistance = (ctx: SimContext): number => {
  const team = ctx.services.team;
  const ratio = ctx.config.balance.combat.approachRatio;
  let stop = Number.POSITIVE_INFINITY;
  team.cats().forEach((c, i) => {
    const range = team.resolveFor(c.cat)?.range ?? UNARMED_RANGE;
    const back = i === 0 ? 0 : Math.abs(team.formationOffset(i).dy);
    stop = Math.min(stop, range * ratio + back);
  });
  return Number.isFinite(stop) ? Math.max(MIN_STOP, stop) : UNARMED_RANGE * ratio;
};

/** Cible a rejoindre en AutoMove : celle du chat, sinon la plus proche cible tirable dans aimMaxRange. */
const autoMoveTarget = (ctx: SimContext, leader: With<'cat'>): { x: number; y: number } | null => {
  let e = leader.cat.targetId !== null ? ctx.rt.world.get(leader.cat.targetId) : undefined;
  if (!e || !isShootable(ctx, e)) {
    const id = ctx.rt.targetGrid.nearest(leader.transform.x, leader.transform.y, ctx.config.sim.aimMaxRange, (cid) => {
      const o = ctx.rt.world.get(cid);
      return !!o && isShootable(ctx, o);
    });
    e = id !== null ? ctx.rt.world.get(id) : undefined;
  }
  return e ? { x: e.transform.x, y: e.transform.y } : null;
};

/**
 * Chef : manuel prioritaire (doigt pose), sinon AutoMove vers la cible jusqu'a `approachRatio x portee` de l'arme,
 * sinon retour doux au point de depart du mode. Integration + decors solides + bornes d'arene (barriere en Monster).
 */
export const ControlSystem: System = {
  id: 'control',
  update(ctx: SimContext, dt: number): void {
    moveLeader(ctx, dt);
    // Une seule passe de separation pour toute l'equipe, APRES tous les deplacements du tic (chef inclus).
    separateCats(ctx, ctx.services.team.cats());
  },
};

const moveLeader = (ctx: SimContext, dt: number): void => {
  {
    const leader = ctx.services.team.leader();
    if (!leader) return;
    const cat = leader.cat;
    const control = ctx.rt.control;
    if (!control.canMove) {
      cat.moving = false;
      return;
    }
    const speed = ctx.stats.getNumber('moveSpeed');
    const input = ctx.rt.input;
    const mag = Math.hypot(input.moveX, input.moveY);
    let vx = 0;
    let vy = 0;
    if (input.active && mag > MANUAL_DEADZONE) {
      const k = Math.min(1, mag) / mag;
      vx = input.moveX * k * speed;
      vy = input.moveY * k * speed;
    } else if (control.autoMove) {
      const t = autoMoveTarget(ctx, leader);
      const stop = teamStopDistance(ctx);
      const px = leader.transform.x;
      const py = leader.transform.y;
      // Un point fourni par le mode (avancee sur le chemin) se rejoint a pleine vitesse ; le retour par defaut est lent.
      const modeHome = ctx.modes.current.homePoint?.(ctx) ?? null;
      const goal = t ?? modeHome ?? defaultHome(ctx);
      const v = t || modeHome ? speed : speed * HOME_SPEED_RATIO;
      const arrive = t ? stop : HOME_ARRIVE;
      const gx = goal.x;
      const gy = goal.y;
      const d = dist(px, py, gx, gy);
      if (d > arrive) {
        const step = Math.min(v, (d - arrive) / dt);
        vx = ((gx - px) / d) * step;
        vy = ((gy - py) / d) * step;
      }
    }
    const px = leader.transform.x;
    const py = leader.transform.y;
    leader.transform.x += vx * dt;
    leader.transform.y += vy * dt;
    resolveSolids(ctx, leader, leader.collider?.radius ?? 0.55);
    clampToBounds(ctx, leader);
    const actual = Math.hypot(leader.transform.x - px, leader.transform.y - py) / dt;
    cat.moving = actual > MOVING_SPEED;
    if (cat.moving && cat.targetId === null) {
      const dx = leader.transform.x - px;
      const dy = leader.transform.y - py;
      if (Math.hypot(dx, dy) > HEADING_MOVE_EPS) leader.transform.rot = rotateToward(leader.transform.rot, Math.atan2(dy, dx), HEADING_TURN_SPEED * dt);
    }
  }
};
